import React, { useState, useEffect } from 'react';
import { Edit3, Save, X, Plus, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CarouselEditor({ onClose }) {
  const [lines, setLines] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [newLine, setNewLine] = useState({ name: '', code: '', station_count: 20, line_type: 'carousel', production_leader: '' });

  const fetchLines = async () => {
    try {
      const res = await axios.get(`${API}/api/lines`);
      setLines(res.data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchLines(); }, []);

  const startEdit = (line) => {
    setEditingId(line.id);
    setEditForm({
      name: line.name,
      code: line.code,
      station_count: line.station_count,
      production_leader: line.production_leader || '',
    });
  };

  const saveEdit = async () => {
    try {
      await axios.put(`${API}/api/lines/${editingId}`, editForm);
      toast.success('Line updated');
      setEditingId(null);
      fetchLines();
    } catch (err) { toast.error('Update failed'); }
  };

  const handleAdd = async () => {
    if (!newLine.name || !newLine.code) { toast.error('Name and code are required'); return; }
    try {
      await axios.post(`${API}/api/lines`, newLine);
      toast.success('Line added');
      setShowAdd(false);
      setNewLine({ name: '', code: '', station_count: 20, line_type: 'carousel', production_leader: '' });
      fetchLines();
    } catch (err) { toast.error('Add failed'); }
  };

  const handleDelete = async (lineId, lineName) => {
    if (!window.confirm(`Delete "${lineName}"? This will also delete all stations and assignments.`)) return;
    try {
      await axios.delete(`${API}/api/lines/${lineId}`);
      toast.success('Line deleted');
      fetchLines();
    } catch (err) { toast.error('Delete failed'); }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="carousel-editor">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-slate-800">Carousel Lines</h3>
          <p className="text-xs text-slate-500 mt-0.5">Manage production carousel lines, station counts, and team leaders</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowAdd(!showAdd)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            data-testid="add-line-btn">
            <Plus className="w-3.5 h-3.5" /> Add Line
          </button>
          {onClose && (
            <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Add new line form */}
      {showAdd && (
        <div className="px-5 py-3 bg-blue-50/50 border-b border-blue-100">
          <div className="grid grid-cols-5 gap-3">
            <div>
              <label className="text-[10px] font-medium text-slate-500 uppercase">Name</label>
              <input type="text" value={newLine.name} onChange={e => setNewLine(p => ({ ...p, name: e.target.value }))}
                placeholder="Carousel Line 4" className="w-full border rounded-lg px-2.5 py-1.5 text-sm mt-0.5" data-testid="new-line-name" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 uppercase">Code</label>
              <input type="text" value={newLine.code} onChange={e => setNewLine(p => ({ ...p, code: e.target.value }))}
                placeholder="C4" className="w-full border rounded-lg px-2.5 py-1.5 text-sm mt-0.5" data-testid="new-line-code" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 uppercase">Stations</label>
              <input type="number" min="1" value={newLine.station_count} onChange={e => setNewLine(p => ({ ...p, station_count: parseInt(e.target.value) || 1 }))}
                className="w-full border rounded-lg px-2.5 py-1.5 text-sm mt-0.5" data-testid="new-line-stations" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 uppercase">Leader</label>
              <input type="text" value={newLine.production_leader} onChange={e => setNewLine(p => ({ ...p, production_leader: e.target.value }))}
                placeholder="Name" className="w-full border rounded-lg px-2.5 py-1.5 text-sm mt-0.5" data-testid="new-line-leader" />
            </div>
            <div className="flex items-end gap-1.5">
              <button onClick={handleAdd} className="px-3 py-1.5 text-xs font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700" data-testid="save-new-line-btn">
                <Save className="w-3.5 h-3.5 inline mr-1" /> Save
              </button>
              <button onClick={() => setShowAdd(false)} className="px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-100 rounded-lg">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Lines list */}
      <div className="divide-y divide-slate-100">
        {lines.map(line => (
          <div key={line.id} className="px-5 py-3 hover:bg-slate-50/50 group" data-testid={`line-row-${line.id}`}>
            {editingId === line.id ? (
              <div className="grid grid-cols-5 gap-3 items-end">
                <div>
                  <label className="text-[10px] font-medium text-slate-500 uppercase">Name</label>
                  <input type="text" value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                    className="w-full border border-blue-300 rounded-lg px-2.5 py-1.5 text-sm mt-0.5 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                    data-testid={`edit-line-name-${line.id}`} />
                </div>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 uppercase">Code</label>
                  <input type="text" value={editForm.code} onChange={e => setEditForm(p => ({ ...p, code: e.target.value }))}
                    className="w-full border border-blue-300 rounded-lg px-2.5 py-1.5 text-sm mt-0.5 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                    data-testid={`edit-line-code-${line.id}`} />
                </div>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 uppercase">Stations</label>
                  <input type="number" min="1" value={editForm.station_count} onChange={e => setEditForm(p => ({ ...p, station_count: parseInt(e.target.value) || 1 }))}
                    className="w-full border border-blue-300 rounded-lg px-2.5 py-1.5 text-sm mt-0.5 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                    data-testid={`edit-line-stations-${line.id}`} />
                </div>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 uppercase">Leader</label>
                  <input type="text" value={editForm.production_leader} onChange={e => setEditForm(p => ({ ...p, production_leader: e.target.value }))}
                    className="w-full border border-blue-300 rounded-lg px-2.5 py-1.5 text-sm mt-0.5 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                    data-testid={`edit-line-leader-${line.id}`} />
                </div>
                <div className="flex items-end gap-1.5">
                  <button onClick={saveEdit} className="px-3 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-testid={`save-line-${line.id}`}>
                    <Save className="w-3.5 h-3.5 inline mr-1" /> Save
                  </button>
                  <button onClick={() => setEditingId(null)} className="px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-100 rounded-lg">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-slate-800 text-white flex items-center justify-center text-lg font-bold">
                    {line.code}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-slate-800">{line.name}</div>
                    <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500">
                      <span>{line.station_count} stations</span>
                      <span className="text-slate-300">|</span>
                      <span>{line.line_type}</span>
                      {line.production_leader && (
                        <>
                          <span className="text-slate-300">|</span>
                          <span className="text-blue-600 font-medium">Leader: {line.production_leader}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => startEdit(line)}
                    className="p-2 hover:bg-blue-50 rounded-lg" data-testid={`edit-line-btn-${line.id}`}>
                    <Edit3 className="w-4 h-4 text-blue-500" />
                  </button>
                  <button onClick={() => handleDelete(line.id, line.name)}
                    className="p-2 hover:bg-red-50 rounded-lg" data-testid={`delete-line-btn-${line.id}`}>
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {lines.length === 0 && (
        <div className="px-5 py-8 text-center text-sm text-slate-400">
          No carousel lines configured.
        </div>
      )}
    </div>
  );
}
